import { Button, IconMedallion, Modal, Sheet } from '@ai-tutor/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEntitlementsUsage } from '@/hooks/account/useBilling';
import { appEvents, type BudgetExceededPayload } from '@/lib/app-events';
import { describeBudgetLimit, tierOf, type LimitCta } from '@/lib/budget-limit';
import { track } from '@/config/observability';
import { queryKeys } from '@/config/query-client';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

/**
 * Global limit modal.
 *
 * Mounted once at the app root. Listens for `budget-exceeded` events emitted by
 * the API client on a 402 and explains WHY the action was blocked - which
 * daily/monthly limit was hit and when it resets. Enriches the copy with the
 * specific feature's usage from `/entitlements/usage`, and degrades gracefully
 * to the 402 payload if that data hasn't loaded.
 *
 * The same query supplies the tier, and the buttons follow it: Free gets
 * "Upgrade to Plus", Plus only "Got it" (never an upgrade to the plan it
 * has), and an unknown tier a neutral "See plans". Copy lives in
 * `lib/budget-limit.ts`, shared with the inline `BudgetExceededCard`.
 *
 * Dedup: ignores additional events for ~5s while a modal is already open, so
 * several in-flight requests hitting the cap at once don't stack.
 */

export function BudgetExceededDialog() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<BudgetExceededPayload | null>(null);
  const lastTriggeredAt = useRef<number>(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usage = useEntitlementsUsage();
  const phone = useMediaQuery('(max-width: 639.98px)');
  // The listener below is registered once; read the tier through a ref.
  const usageRef = useRef(usage.data);
  usageRef.current = usage.data;

  useEffect(() => {
    return appEvents.on('budget-exceeded', event => {
      const now = Date.now();
      if (now - lastTriggeredAt.current < 5_000) return;
      lastTriggeredAt.current = now;
      setPayload(event.payload);
      setOpen(true);
      track('paywall_shown', {
        surface: 'limit_modal',
        reason: event.payload?.reason ?? 'unknown',
        feature: event.payload?.feature ?? 'unknown',
        tier: tierOf(usageRef.current),
      });
      // Refetch so the modal reflects the just-hit feature's remaining/reset.
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.billing.all, 'entitlements-usage'],
      });
    });
  }, [queryClient]);

  const onUpgrade = () => {
    track('upgrade_clicked', {
      surface: 'limit_modal',
      feature: payload?.feature ?? undefined,
    });
    setOpen(false);
    navigate('/settings?tab=payment');
  };

  const copy = describeBudgetLimit({
    reason: payload?.reason,
    feature: payload?.feature,
    window: payload?.window,
    resetAt: payload?.reset_at,
    usage: usage.data,
  });

  const body = (
    <>
      <div className='mb-4 flex items-center gap-3'>
        <IconMedallion tone='warning' size='sm' className='h-10 w-10'>
          <Sparkles className='h-5 w-5' />
        </IconMedallion>
        <h2 className='text-base font-semibold text-foreground'>
          {copy.title}
        </h2>
      </div>
      <p className='mb-5 text-sm text-muted-foreground'>{copy.body}</p>
      <DialogActions
        cta={copy.cta}
        stacked={phone}
        onClose={() => setOpen(false)}
        onUpgrade={onUpgrade}
      />
    </>
  );

  // A bottom sheet on a phone, as the app's other phone dialogs are, with
  // full-width 44px buttons. It also has to land ON TOP: as a centred Modal
  // it opened underneath the lesson's chat sheet (both z-50, the chat later
  // in the DOM), so hitting the chat limit on a phone showed nothing at all.
  // Raised a layer (`z-[60]`) rather than relying on mounting after whatever
  // is open: the chat sheet's portal can be created later than this one.
  if (phone) {
    return (
      <Sheet
        isOpen={open}
        onClose={() => setOpen(false)}
        size='auto'
        ariaLabel={copy.ariaLabel}
        showHeader={false}
        overlayClassName='z-[60]'
      >
        <div className='px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5'>
          {body}
        </div>
      </Sheet>
    );
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      size='sm'
      // Above the lesson's chat sheet on a tablet too (z-50, and mounted
      // whenever the lesson page was) - an app-wide interruption must not
      // depend on mounting after whatever is already open.
      overlayClassName='z-[60]'
      ariaLabel={copy.ariaLabel}
      showCloseButton={false}
    >
      <div className='p-6'>{body}</div>
    </Modal>
  );
}

function DialogActions({
  cta,
  stacked,
  onClose,
  onUpgrade,
}: {
  cta: LimitCta;
  /** Full-width, main action first - the phone sheet. */
  stacked: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}) {
  if (stacked) {
    return (
      <div className='flex flex-col gap-2'>
        {cta === 'dismiss' ? (
          <Button className='h-11 w-full' onClick={onClose}>
            Got it
          </Button>
        ) : (
          <>
            <Button className='h-11 w-full' onClick={onUpgrade}>
              {cta === 'upgrade' ? 'Upgrade to Plus' : 'See plans'}
            </Button>
            <Button variant='ghost' className='h-11 w-full' onClick={onClose}>
              Not now
            </Button>
          </>
        )}
      </div>
    );
  }
  if (cta === 'dismiss') {
    return (
      <div className='flex justify-end'>
        <Button size='sm' onClick={onClose}>
          Got it
        </Button>
      </div>
    );
  }
  return (
    <div className='flex justify-end gap-2'>
      <Button variant='ghost' size='sm' onClick={onClose}>
        Not now
      </Button>
      <Button size='sm' onClick={onUpgrade}>
        {cta === 'upgrade' ? 'Upgrade to Plus' : 'See plans'}
      </Button>
    </div>
  );
}
